/**
 * 被写界深度の焦点距離とボケの落ち方。
 * シェーダ側と同じ式をここでも計算できるようにして、テストと描画で食い違わないようにする。
 */

/** 壁に鼻をつけてもマクロ撮影にならない下限 */
export const DOF_MIN_FOCUS = 0.25;
/** 空やフォグの向こうを「無限遠」として扱わない上限 */
export const DOF_MAX_FOCUS = 80;
/** 照準が空のときの風景用ピント（フォグが効き始める手前） */
export const DOF_LANDSCAPE_FOCUS = 36;
/** 焦点が目標へ追従する速さ（大きいほどキビキビ） */
export const DOF_FOCUS_SPEED = 7;

export interface DofFalloff {
  /** ピント面より手前で、まだシャープな距離 */
  nearStart: number;
  /** nearStart からフルボケまでの幅 */
  nearFalloff: number;
  /** ピント面より奥で、まだシャープな距離 */
  farStart: number;
  /** farStart からフルボケまでの幅 */
  farFalloff: number;
}

export function resolveFocusDistance(hitDistance: number | null | undefined): number {
  if (hitDistance == null || !Number.isFinite(hitDistance) || hitDistance > DOF_MAX_FOCUS) {
    return DOF_LANDSCAPE_FOCUS;
  }
  return Math.min(DOF_MAX_FOCUS, Math.max(DOF_MIN_FOCUS, hitDistance));
}

export function smoothFocusDistance(
  current: number,
  target: number,
  dt: number,
  speed = DOF_FOCUS_SPEED
): number {
  const t = 1 - Math.exp(-speed * Math.max(0, dt));
  return current + (target - current) * t;
}

/** 遠い目標ほどシャープ帯を広げ、近くを見ているときは手前／奥をはっきり分ける。 */
export function dofFalloff(focus: number): DofFalloff {
  const distance = Math.max(DOF_MIN_FOCUS, focus);
  return {
    nearStart: 0.5 + distance * 0.04,
    nearFalloff: 1.2 + distance * 0.08,
    farStart: Math.max(2.5, distance * 0.25),
    farFalloff: Math.max(18, distance * 1.2),
  };
}

export function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/** 0 = 合焦、1 = 最大ボケ。シェーダの CoC と同じ。 */
export function circleOfConfusion(depth: number, focus: number): number {
  const band = dofFalloff(focus);
  const nearC =
    1 -
    smoothstep(focus - band.nearStart - band.nearFalloff, focus - band.nearStart, depth);
  const farC = smoothstep(focus + band.farStart, focus + band.farStart + band.farFalloff, depth);
  return Math.max(nearC, farC);
}
